import React, { useEffect, useState } from 'react';
import { MRT_Cell } from 'material-react-table';
import { AdAccountType, LoadAccount } from './useLoadAccount';
import {
  styled,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BlockIcon from '@mui/icons-material/Block';
import { useModalStore } from 'src/stores/ModalStore';
import { CommonButton, NextButton } from '../common/CommonStyled';
import { deleteUser, postApproveUser, postInactiveUser } from 'src/api/Admin';
import { useAdAccountStore } from 'src/stores/AdAccountStore';

const SelectWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
});

const statusOptions: {
  label: string;
  icon: React.ReactElement<{ style?: React.CSSProperties }>;
  color: string;
  bgc:string;
}[] = [
  { label: '승인', icon: <CheckCircleIcon />, color: '#1976FF', bgc:'#D1E4FF' },
  { label: '대기', icon: <RemoveCircleIcon />, color: '#209373', bgc: '#D6FCF2' },
  { label: '차단', icon: <BlockIcon sx={{ transform: 'rotate(90deg)' }}/>, color: '#FF6224', bgc: '#FFE6DC' },
  { label: '삭제', icon: <CancelIcon />, color: '#FF5A5A', bgc: '#FFCCCC' },
];
const selectableStatusOptions = statusOptions.filter(opt => opt.label !== '대기');

interface Props {
  cell: MRT_Cell<AdAccountType, string>;
}

const AdAccountTable_SelectBox = ({ cell }: Props) => {
  const value = cell.getValue() as string;
  const row = cell.row.original;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selected, setSelected] = useState(value);
  const setModalField = useModalStore((state)=>state.setField)
  const setDataField = useAdAccountStore((state)=>state.setField)
  const reset = useModalStore((state)=>state.reset)
  const page = useAdAccountStore((state)=>state.page)
  const pageSize = useAdAccountStore((state)=>state.pageSize)
  const status = useAdAccountStore((state)=>state.status)

  useEffect(()=>{
    return(()=>{
      reset()
    })
  //eslint-disable-next-line
  },[])

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const refetchData = async () => {
    const result = await LoadAccount(status,page,pageSize);
    setDataField('data',result.data)
    setDataField('totalpage',result.totalPage)
    setDataField('totalCount',result.totalCount)
    setDataField('approveCount',result.approved)
    setDataField('pendingCount',result.pending)
    setDataField('inactiveCount',result.inactive)
  };

  const handleDelete = async()=>{
    await deleteUser(row.user_id)
    setModalField('title',`'${row.user_name}'계정이 삭제되었습니다.`)
    setModalField('content','')
    setModalField('footer',
      <>
      <NextButton style={{maxWidth:'68px', maxHeight:'40px'}} onClick={()=>{setModalField('open',false)
        refetchData()}}>확인</NextButton>
      </>)
  }

  const handleSelect = async (newStatus: string) => {
    if (newStatus === '삭제') {
      setModalField('type','BASIC')
      setModalField('title',`'${row.user_name}'계정을 삭제하시겠습니까?`)
      setModalField('width','466px')
      setModalField('content','삭제 후에는 복구가 불가합니다.')
      setModalField('footer',
        <>
          <NextButton style={{maxWidth:'68px', maxHeight:'40px'}} onClick={()=>handleDelete()}>확인</NextButton>
          <CommonButton style={{maxWidth:'68px', maxHeight:'40px', border:'1px solid #767676', backgroundColor:'#E3E3E3', color:'#1E1E1E'}} onClick={()=>setModalField('open',false)}>취소</CommonButton>
        </>)
      setModalField('open', true);
      return
    }
    if (newStatus === '승인'){
      await postApproveUser(row.user_id)
      refetchData()
    }
    if (newStatus === '차단'){
      await postInactiveUser(row.user_id)
      refetchData()
    }
    // setSelected(newStatus);
    handleClose();
  };

  const current = statusOptions.find((s) => s.label === selected)!;
  
  useEffect(() => {
  setSelected(value);
}, [value]);

  return (
    <SelectWrapper>
      <Button
        variant="outlined"
        onClick={handleOpen}
        startIcon={current.icon}
        endIcon={<KeyboardArrowDownIcon/>}
        sx={{
          color: current.color,
          borderColor: current.color,
          backgroundColor: current.bgc,
          maxWidth: 90,
          textTransform: 'none',
          whiteSpace:'nowrap',
          '& .MuiButton-startIcon': {
            marginRight: '4px',
            },
            '& .MuiButton-endIcon': {
            marginLeft: '4px',
            },
        fontWeight:'bold'
        }}
      >
        {current.label}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        MenuListProps={{
          sx: {
            py: 0,
            px: 0,
          },
        }}
        PaperProps={{
          sx: {
            border: '1px solid #7B7B84',
            color: current.color,
            maxWidth: 90,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            boxShadow: 3,
            mt: '-1px',
            boxSizing: 'border-box',
          },
        }}
      >
        {/* ✅ 첫 번째 항목: 현재 선택된 항목 */}
        <MenuItem
          selected
          sx={{
            px: 0,
            width: '90px',
            display: 'flex',
            alignItems: 'center',
            borderBottom: statusOptions.length > 1 ? '1px solid #7B7B84' : 'none',
            height: '36.5px',
            boxSizing: 'border-box',
            '&.Mui-selected': {
              backgroundColor: current.bgc,
            },
          }}
        >
          <div
            style={{
              width: 20,
              height: 20,
              color: current.color,
              marginLeft: '8px',
              marginRight: '4px',
            }}
          >
            {React.cloneElement(current.icon, { style: { fontSize: 20 } })}
          </div>
          <span
            style={{
              fontSize: 14,
              textAlign: 'left',
              flex: 1,
              color: current.color,
            }}
          >
            {current.label}
          </span>
        </MenuItem>

        {/* ✅ 나머지 항목들: current 제외 */}
        {selectableStatusOptions
          .filter((option) => option.label !== current.label)
          .map((option, index, filtered) => (
            <MenuItem
              key={option.label}
              selected={option.label === selected}
              onClick={() => handleSelect(option.label)}
              sx={{
                px: 0,
                width: '90px',
                display: 'flex',
                alignItems: 'center',
                borderBottom:
                  index !== filtered.length - 1 ? '1px solid #7B7B84' : 'none',
                height: '36.5px',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  color: option.color,
                  marginLeft: '8px',
                  marginRight: '4px',
                }}
              >
                {React.cloneElement(option.icon, { style: { fontSize: 20 } })}
              </div>
              <span
                style={{
                  fontSize: 14,
                  textAlign: 'left',
                  flex: 1,
                  color: option.color,
                }}
              >
                {option.label}
              </span>
            </MenuItem>
          ))}
      </Menu>
    </SelectWrapper>
  );
};

export default AdAccountTable_SelectBox;
